/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  forwardRef,
  useCallback,
  useRef,
  useEffect,
} from 'react'

import type { Ref } from 'react'
import clsx from 'clsx'

import Creatable from 'react-select/creatable'
import { components } from 'react-select'
import { withAsyncPaginate } from 'react-select-async-paginate'

import { Loader, Chip } from 'components'
import { IconSearch, IconPlusCircle, IconWarningCircle } from 'components/Icons'
import { getLabel } from 'helpers/language'

import type { ILoaderOption } from 'types'

import { customStyles } from 'components/AutoSuggest/style'
import i18n from 'config/i18n'
import {
  IProductCategorySingleProps,
  ProductCategoryService,
} from 'features/ProductCategory'

export interface AutoSuggestCreatableProductCategoryProps {
  title?: string
  limit?: number
  message?: string
  placeholder?: string
  selected?: ILoaderOption[]
  className?: string
  limitText?: string
  notFoundText?: string
  options?: ILoaderOption[]
  loadOptions?: (
    searchQuery: string,
    loadedOptions: any,
    options: { page: number },
  ) => Promise<{
    options: any
    hasMore: any
    additional: { page: number }
  }>
  onBlur?: () => void
  onChange: (item: ILoaderOption) => void
  height?: number
  position?: string
  fullWidth?: boolean
  isError?: boolean
  disabled?: boolean
  isMultiSelect?: boolean
  isSearchable?: boolean
}

const CreatableAsyncPaginate = withAsyncPaginate(Creatable)

const AutoSuggestCreatableProductCategory = (
  props: AutoSuggestCreatableProductCategoryProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element => {
  const {
    title = '',
    limit = 20,
    message,
    placeholder,
    limitText,
    notFoundText,
    selected,
    options,
    onBlur = () => {},
    isError,
    isMultiSelect,
    isSearchable,
    onChange,
  } = props

  const fetchProductCategories = async (
    searchQuery: string,
    loadedOptions: unknown[],
    options: { page: number },
  ) => {
    const queryName = i18n.language === 'th' ? 'nameTh' : 'nameEn'
    const payload = {
      ...(searchQuery &&
        i18n.language === 'th' && {
          'nameTh[like]': `%${searchQuery.trim()}%`,
        }),
      ...(searchQuery &&
        i18n.language === 'en' && {
          'nameEn[like]': `%${searchQuery.trim()}%`,
        }),
      _limit: 10,
      _page: options.page,
      _sort: queryName,
      _order: 'ASC',
      isActive: true,
    }

    const { body: _data, totals } = await ProductCategoryService.findAll(
      payload,
    )

    const currentTotal = loadedOptions.length + _data.length

    return {
      options: _data.map((data: IProductCategorySingleProps) => ({
        label: data.name,
        key: data.id,
        ...data,
      })),
      hasMore: currentTotal < totals,
      additional: {
        page: payload._page + 1,
      },
    }
  }

  const [isAddingInProgress, setIsAddingInProgress] = useState(false)
  const [value, onChangeCreate] = useState<any>(null)

  const prevOptionsRef = useRef<ILoaderOption[]>()
  const maxLength = 30

  useEffect(() => {
    prevOptionsRef.current = selected || value
  }, [selected, value])

  const handleChange = (item: unknown) => {
    onChangeCreate(item as ILoaderOption)
    onChange(item as ILoaderOption)
  }

  const IndicatorsContainerCustom = (props: any) => {
    const { isLoading } = props.selectProps

    return (
      <div className='select__indicators-container--top'>
        {isLoading ? <Loader size='sm' /> : <IconSearch variants='sm' />}
      </div>
    )
  }

  const MultiValueCustom = (props: any) => {
    return (
      <Chip
        className='chip__search__multi'
        onClose={props.removeProps.onClick}
        label={props.data.label}
        data-testid={`chip-${props.data.key}`}
      />
    )
  }

  const addNewOption = async (inputValue: any) => {
    const newOption = [
      {
        key: inputValue,
        label: inputValue,
        value: inputValue,
      },
    ]

    return newOption
  }

  const renderDuplicate = (base: string, title: string, inputValue: string) => {
    const titleText = title && (
      <span className={`${base}__creatable-option-duplicate`}>{title}</span>
    )

    return (
      <div
        className={`${base}__creatable-option-layout-disable`}
        data-testid='creatable-option-layout'
      >
        <IconWarningCircle variants='sm' className='icon--red' />
        <div className={`${base}____creatable-text`}>
          {titleText}
          <span
            className={`${base}__duplicate-input-value`}
          >{` "${inputValue}" `}</span>
          <span className={`${base}__creatable-option-duplicate`}>
            {getLabel('common:duplicate.autosuggest')}
          </span>
        </div>
      </div>
    )
  }

  const formatCreateLabel = (inputValue: string) => {
    const base = 'select'
    const renderNotFoundAddition = (
      <div
        className={`${base}__creatable-option-layout`}
        data-testid='creatable-option-layout'
      >
        <IconPlusCircle variants='sm' className='icon--gray-more-option' />
        <div className={`${base}____creatable-text`}>
          <span className={`${base}__creatable-option-not-found-text`}>
            {notFoundText}
          </span>
          <span
            className={`${base}__new-input-value`}
          >{` "${inputValue}" `}</span>
          <span className={`${base}__creatable-option-not-found-text`}>
            {getLabel('common:orNot.label')}
          </span>
        </div>
      </div>
    )

    return renderNotFoundAddition
  }

  const NoOptionsMessage = (props: any, title: string) => {
    const base = 'select'
    const inputValue = props.selectProps.inputValue as string

    return (
      <components.NoOptionsMessage {...props}>
        {inputValue ? renderDuplicate(base, title, inputValue) : props.children}
      </components.NoOptionsMessage>
    )
  }

  const setNewOptions = (options: ILoaderOption[], limit: number) => {
    if (options.length <= limit) {
      const result: ILoaderOption[] = options.filter(
        (thing: ILoaderOption, index, self) =>
          index ===
          self.findIndex(
            (t) => t.label.toLowerCase() === thing.label.toLowerCase(),
          ),
      )

      onChangeCreate(result)
      handleChange(result)
    }
  }

  const onCreateOption = useCallback(async (inputValue: string) => {
    if (typeof inputValue === 'object') {
      handleChange(inputValue)
    }
    if (typeof inputValue === 'string' && inputValue.length <= maxLength) {
      setIsAddingInProgress(true)
      const newOption = await addNewOption(inputValue)
      setIsAddingInProgress(false)
      if (prevOptionsRef.current) {
        const options: ILoaderOption[] = [
          ...prevOptionsRef.current,
          ...newOption,
        ]

        setNewOptions(options, limit)
      } else {
        onChangeCreate(newOption)
        handleChange(newOption)
      }
    }
  }, [])

  const Input = (props: any) => {
    return (
      <components.Input
        {...props}
        maxLength={maxLength}
        autoFocus={props.selectProps.menuIsOpen}
      />
    )
  }

  const Menu = (props: any) => {
    const optionSelectedLength = props.getValue().length || 0

    const renderOption =
      optionSelectedLength < limit ? (
        props.children
      ) : (
        <div className='select__limit-option-layout'>
          <span className='select__limit-option-text'>{limitText}</span>
        </div>
      )

    return <components.Menu {...props}>{renderOption}</components.Menu>
  }

  return (
    <>
      <CreatableAsyncPaginate
        value={selected || value}
        selectRef={ref}
        options={options}
        classNamePrefix='custom-select-creatable'
        styles={customStyles(props)}
        SelectComponent={Creatable}
        isDisabled={isAddingInProgress}
        loadOptions={fetchProductCategories as never}
        placeholder={placeholder}
        key={JSON.stringify(selected)}
        getOptionValue={(option) => option.key}
        getOptionLabel={(option) => option.label}
        onCreateOption={onCreateOption}
        onChange={onCreateOption}
        onBlur={onBlur}
        additional={{
          page: 1,
        }}
        components={{
          MultiValue: MultiValueCustom,
          IndicatorsContainer: IndicatorsContainerCustom,
          IndicatorSeparator: null,
          Input,
          Menu,
          NoOptionsMessage: (props: any) => NoOptionsMessage(props, title),
        }}
        formatCreateLabel={formatCreateLabel}
        debounceTimeout={300}
        isError={isError}
        isMulti={isMultiSelect}
        isSearchable={isSearchable}
      />

      {message && (
        <span
          className={clsx('text-input__message', {
            'text-input__error': isError,
          })}
          data-testid='text-input-message'
        >
          {message}
        </span>
      )}
    </>
  )
}

export default forwardRef(AutoSuggestCreatableProductCategory)
